<script setup>
import { ref, computed } from 'vue';
import { VPhoneInput } from 'v-phone-input';
import { getSupportedRegionCodes } from 'awesome-phonenumber';
import { ISO3166Countries } from '@nova/core';

/** Some ISO-3166 countries (e.g. Antarctica) have no telephone calling code and
 * aren't part of v-phone-input's country list. Forwarding one of those anyway
 * makes it silently fall back to an unrelated country/flag. */
const supportedPhoneRegionCodes = new Set(getSupportedRegionCodes());

import 'flag-icons/css/flag-icons.min.css';
import 'v-phone-input/dist/v-phone-input.css';

const props = defineProps({
  /**
   * @model
   */
  modelValue: {
    type: String,
    required: false,
    default: ''
  },
  country: {
    type: String,
    required: false,
    default: ''
  },
  /**
   * Field label
   */
  label: {
    type: String,
    required: false,
    default: ''
  },
  /**
   * Field placeholder
   */
  placeholder: {
    type: String,
    required: false,
    default: ''
  },
  /**
   * Invalid message
   */
  invalidMessage: {
    type: Function,
    required: false,
    default: options => {
      const { example } = options ?? {};

      return `This is not a valid phone number ${example ? `(example: "${example}")` : ''}`;
    }
  },
  validateOn: {
    type: String,
    required: false,
    validator: value => {
      return ['blur', 'input', 'blur invalid-input', 'input invalid-input'].includes(value);
    },
    default: 'blur invalid-input'
  },
  /**
   * Default country code (ISO2 format like 'US', or ISO3 format like 'USA')
   * The component will automatically convert ISO3 to ISO2 if needed
   */
  defaultCountry: {
    type: String,
    required: false,
    default: ''
  },
  /**
   * Vuetify variant
   */
  variant: {
    type: String,
    required: false,
    default: 'outlined'
  },
  /**
   * Vuetify color
   */
  color: {
    type: String,
    required: false,
    default: 'primary'
  },
  /**
   * Country icon mode: 'svg', 'sprite', or 'text'
   */
  countryIconMode: {
    type: String,
    required: false,
    default: 'svg'
  },
  /**
   * Phone number format: 'international', 'national', or 'e164'
   */
  modelFormat: {
    type: String,
    required: false,
    default: 'international'
  },
  /**
   * Whether the field is required
   */
  required: {
    type: Boolean,
    required: false,
    default: false
  },
  /**
   * Whether the field is disabled
   */
  disabled: {
    type: Boolean,
    required: false,
    default: false
  },
  /**
   * Vuetify validation rules array
   * Each rule is a function that takes a value and returns true if valid, or an error message string if invalid
   */
  rules: {
    type: Array,
    required: false,
    default: () => []
  },
  inputProps: {
    type: Object,
    required: false,
    default: () => ({})
  },
  countryProps: {
    type: Object,
    required: false,
    default: () => ({})
  }
});

const emit = defineEmits(['update:modelValue', 'update:country']);

const phoneInputRef = ref(null);

/**
 * Converts ISO3 country code to ISO2, or returns ISO2 if already in ISO2 format
 * @param {string} countryCode - ISO2 (e.g., 'US') or ISO3 (e.g., 'USA') country code
 * @returns {string} ISO2 country code
 */
const convertToISO2 = countryCode => {
  if (!countryCode) {
    return '';
  }

  const upperCode = countryCode.toUpperCase();

  // If it's already 2 characters, assume it's ISO2
  if (upperCode.length === 2) {
    return upperCode;
  }

  // If it's 3 characters, try to convert from ISO3 to ISO2
  if (upperCode.length === 3) {
    const country = ISO3166Countries[upperCode];
    return country?.iso2 || '';
  }

  // Fallback: return as-is if format is unexpected
  return upperCode;
};

const FALLBACK_PHONE_REGION = 'US';

/**
 * Converts a country code to ISO2 format. Falls back to FALLBACK_PHONE_REGION when a
 * country code was given but isn't one v-phone-input recognizes (e.g. it has no telephone
 * calling code, like Antarctica). Returns '' only when no country code was given at all,
 * so consumers that don't pass a country/defaultCountry keep today's unforced behavior.
 */
const toSupportedPhoneRegion = countryCode => {
  if (!countryCode) {
    return '';
  }

  const iso2 = convertToISO2(countryCode);
  return supportedPhoneRegionCodes.has(iso2) ? iso2 : FALLBACK_PHONE_REGION;
};

/**
 * Computed property that converts defaultCountry prop to ISO2 format
 */
const computedDefaultCountry = computed(() => {
  return toSupportedPhoneRegion(props.defaultCountry);
});

/**
 * Computed property that converts country prop to ISO2 format.
 * Bound to v-phone-input's `country` so a parent can control the
 * displayed flag (e.g. keep it in sync while no phone number has been entered yet).
 */
const computedCountry = computed(() => {
  return toSupportedPhoneRegion(props.country);
});

/**
 * Handles input changes
 */
const handleModelValueChange = value => {
  emit('update:modelValue', value);
};

/**
 * Handles country change
 */
const handleCountryChanged = country => {
  emit('update:country', country);
};
</script>

<template>
  <div class="phone-number-field-v2">
    <v-phone-input
      ref="phoneInputRef"
      :color="color"
      :country="computedCountry"
      :country-icon-mode="countryIconMode"
      :default-country="computedDefaultCountry"
      :disabled="disabled"
      :invalid-message="invalidMessage"
      :label="label"
      :model-format="modelFormat"
      :model-value="modelValue"
      :phone-props="inputProps"
      :country-props="countryProps"
      :placeholder="placeholder"
      :rules="rules"
      :validate-on="validateOn"
      :variant="variant"
      v-bind="$attrs"
      @update:model-value="handleModelValueChange"
      @update:country="handleCountryChanged"></v-phone-input>
  </div>
</template>
